<template>
  <v-container class="pa-3" style="max-width:1100px">

    <!-- ── GATE PASSWORD ──────────────────────────────────────────────── -->
    <v-card v-if="!autenticato" class="rounded-xl elevation-2 pa-6 mx-auto" style="max-width:360px;margin-top:80px">
      <div class="text-center mb-4">
        <v-icon size="48" color="primary">mdi-database-lock</v-icon>
        <div class="text-h6 font-weight-bold mt-2">Admin Realm</div>
        <div class="text-caption grey--text">Accesso riservato</div>
      </div>
      <v-text-field
        v-model="passwordInput"
        label="Password admin"
        :type="mostraPassword ? 'text' : 'password'"
        outlined
        rounded
        hide-details
        class="mb-3"
        @keyup.enter="verificaPassword"
        :append-icon="mostraPassword ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append="mostraPassword = !mostraPassword"
      />
      <v-alert v-if="errorePassword" type="error" text dense rounded class="mb-3">
        Password errata
      </v-alert>
      <v-btn color="primary" rounded block @click="verificaPassword">Accedi</v-btn>
    </v-card>

    <!-- ── PANNELLO ADMIN ─────────────────────────────────────────────── -->
    <template v-else>

      <!-- Header + ricerca -->
      <v-card class="rounded-xl elevation-1 mb-3 pa-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-database-search</v-icon>
            <span class="text-body-1 font-weight-bold">Admin Realm</span>
          </div>
          <v-btn text color="error" small @click="logout">
            <v-icon left small>mdi-logout</v-icon>
            Esci
          </v-btn>
        </div>

        <v-row dense>
          <!-- Partizione -->
          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="partizione"
              :items="listaPartizioni"
              label="Partizione"
              outlined
              dense
              rounded
              hide-details
              clearable
              :loading="loadingPartizioni"
              @change="onPartitionChange"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-chip v-if="item === 'PUBLIC'" x-small color="orange darken-2" dark class="font-weight-bold">
                    <v-icon left x-small>mdi-earth</v-icon>
                    PUBLIC
                  </v-chip>
                  <span v-else>{{ item }}</span>
                </v-list-item-content>
              </template>
              <template #selection="{ item }">
                <v-chip v-if="item === 'PUBLIC'" x-small color="orange darken-2" dark class="font-weight-bold mr-1">
                  <v-icon left x-small>mdi-earth</v-icon>
                  PUBLIC
                </v-chip>
                <span v-else>{{ item }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Classe -->
          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="classe"
              :items="listaClassi"
              item-text="nome"
              item-value="nome"
              label="Classe"
              outlined
              dense
              rounded
              hide-details
              clearable
              :loading="loadingClassi"
              :disabled="!partizione"
              @change="onClasseChange"
            />
          </v-col>

          <!-- Campo -->
          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="campoCercato"
              :items="campiClasseCorrente"
              item-text="label"
              item-value="name"
              label="Campo"
              outlined
              dense
              rounded
              hide-details
              clearable
              :disabled="!classe"
            />
          </v-col>

          <!-- Valore + bottone -->
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="valoreCercato"
              :label="campoCercatoInfo && campoCercatoInfo.isOggetto ? ('ID ' + campoCercatoInfo.oggettoTipo) : 'Valore'"
              outlined
              dense
              rounded
              hide-details
              clearable
              :disabled="!classe"
              @keyup.enter="cerca"
            >
              <template #append>
                <v-btn icon x-small text color="primary"
                       :loading="loadingRicerca" :disabled="!classe"
                       @click="cerca">
                  <v-icon small>mdi-magnify</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <v-alert v-if="erroreRicerca" type="error" text dense rounded class="mt-3">
          {{ erroreRicerca }}
        </v-alert>
      </v-card>

      <!-- Risultati -->
      <v-card v-if="risultati.length" class="rounded-xl elevation-1">
        <div class="d-flex align-center justify-space-between px-3 pt-3 pb-1">
          <span class="text-caption grey--text">
            {{ risultati.length }} risultati<span v-if="risultati.length === 200"> (max 200)</span>
            &middot; <span class="primary--text">{{ classe }}</span>
          </span>
          <div class="d-flex align-center">
            <v-btn text x-small class="mr-1" @click="copiaJSON">
              <v-icon x-small left>mdi-content-copy</v-icon>
              JSON
            </v-btn>
            <v-btn outlined x-small color="success" icon class="mr-1" @click="apriNuovo">
              <v-icon x-small>mdi-plus</v-icon>
            </v-btn>
            <v-btn icon x-small text @click="resetRicerca">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="overflow-x-auto px-2 pb-2">
          <table class="admin-table">
            <thead>
              <tr>
                <th style="width:36px"></th>
                <th v-for="col in colonneVisibili" :key="col"
                    @click="ordinaPer(col)" :title="col">
                  {{ col }}
                  <span v-if="sortCol === col" class="sort-icon">
                    {{ sortAsc ? '▲' : '▼' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in risultatiOrdinati" :key="i" class="admin-row">
                <td @click.stop>
                  <v-btn icon x-small text color="error"
                         :loading="eliminando === row['_id']"
                         @click.stop="eliminaRiga(row)">
                    <v-icon x-small>mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
                <td v-for="col in colonneVisibili" :key="col" @click="apriDettaglio(row)">
                  <template v-if="isRef(row[col])">
                    <v-chip x-small color="primary" class="mr-0">
                      {{ row[col].__type }}
                      <span v-if="row[col].__label" class="ml-1" style="opacity:0.7">&middot; {{ row[col].__label }}</span>
                    </v-chip>
                  </template>
                  <template v-else>
                    <span :title="String(row[col] != null ? row[col] : '')">{{ formatVal(row[col]) }}</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- Bottone + anche quando non ci sono risultati ma la classe è selezionata -->
      <div v-if="classe && !risultati.length" class="d-flex justify-end mt-2">
        <v-btn outlined color="success" small @click="apriNuovo">
          <v-icon left small>mdi-plus</v-icon>
          Nuovo record
        </v-btn>
      </div>

      <v-alert v-if="cercatoMaVuoto" type="info" text rounded class="mt-3" dense>
        Nessun risultato trovato.
      </v-alert>

    </template>

    <!-- ── DIALOG DETTAGLIO ───────────────────────────────────────────── -->
    <v-dialog v-model="dialogAperta" max-width="680" scrollable>
      <v-card v-if="rigaSelezionata" class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
          <div class="d-flex align-center">
            <v-icon color="primary" size="20" class="mr-2">mdi-card-account-details-outline</v-icon>
            <span class="text-body-1 font-weight-bold">{{ classe }}</span>
          </div>
          <v-btn icon small text @click="chiudiDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4" style="max-height:70vh">

          <v-alert v-if="errSalvataggio" type="error" text dense rounded class="mb-3">
            {{ errSalvataggio }}
          </v-alert>

          <div v-for="(val, campo) in rigaSelezionata" :key="campo" class="campo-riga">
            <div class="campo-nome text-caption grey--text font-weight-medium">
              {{ campo }}
              <v-icon v-if="campoModificato(campo)" size="10" color="warning" class="ml-1">mdi-circle</v-icon>
            </div>
            <div class="campo-valore">

              <!-- Relazione: chip + bottone picker -->
              <template v-if="isCampoRelazione(campo)">
                <div class="d-flex align-center">
                  <v-chip v-if="isRef(rigaEditabile[campo])" small color="primary"
                          style="cursor:pointer" @click="navigaARef(rigaEditabile[campo])">
                    <v-icon left x-small>mdi-link</v-icon>
                    {{ rigaEditabile[campo].__type }}
                    <span v-if="rigaEditabile[campo].__label" class="ml-1" style="opacity:0.8">&middot; {{ rigaEditabile[campo].__label }}</span>
                    <span class="ml-1 text-caption" style="opacity:0.5">#{{ (rigaEditabile[campo].__id || '').substring(0, 8) }}</span>
                  </v-chip>
                  <span v-else class="text-body-2 grey--text">(nessuno)</span>
                  <v-btn outlined color="primary" class="ml-2" x-small style="min-width:36px"
                         @click="apriPickerRelazione(campo)">…</v-btn>
                </div>
              </template>

              <!-- Campi non editabili: _id, _partition -->
              <template v-else-if="campo === '_id' || campo === '_partition'">
                <span class="text-body-2 grey--text">{{ val != null ? val : '—' }}</span>
              </template>

              <!-- Booleano: switch -->
              <template v-else-if="typeof rigaEditabile[campo] === 'boolean'">
                <v-switch
                  v-model="rigaEditabile[campo]"
                  color="primary"
                  hide-details
                  dense
                  class="mt-0"
                  style="flex:none"
                />
              </template>

              <!-- Testo lungo: textarea -->
              <template v-else-if="typeof rigaEditabile[campo] === 'string' && String(rigaEditabile[campo]).length > 80">
                <v-textarea
                  v-model="rigaEditabile[campo]"
                  outlined
                  dense
                  rounded
                  hide-details
                  rows="3"
                  auto-grow
                  class="campo-input"
                />
              </template>

              <!-- Tutti gli altri campi: text field -->
              <template v-else>
                <v-text-field
                  v-model="rigaEditabile[campo]"
                  outlined
                  dense
                  rounded
                  hide-details
                  class="campo-input"
                  :placeholder="val === null || val === undefined ? '(vuoto)' : ''"
                />
              </template>

            </div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn text small @click="copiaRiga">
            <v-icon left small>mdi-content-copy</v-icon>
            JSON
          </v-btn>
          <v-spacer />
          <v-btn text small @click="chiudiDialog">Annulla</v-btn>
          <v-btn
            color="primary"
            depressed
            small
            rounded
            :loading="salvando"
            :disabled="!haModifiche"
            @click="salva"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Salva
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── DIALOG NUOVO RECORD ──────────────────────────────────────────── -->
    <v-dialog v-model="dialogNuovoAperta" max-width="680" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
          <div class="d-flex align-center">
            <v-icon color="success" size="20" class="mr-2">mdi-plus-circle-outline</v-icon>
            <span class="text-body-1 font-weight-bold">Nuovo {{ classe }}</span>
          </div>
          <v-btn icon small text @click="dialogNuovoAperta = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height:70vh">
          <v-alert v-if="errNuovo" type="error" text dense rounded class="mb-3">
            {{ errNuovo }}
          </v-alert>
          <div v-for="campo in campiNuovoRecord" :key="campo.name" class="campo-riga">
            <div class="campo-nome text-caption grey--text font-weight-medium">{{ campo.name }}</div>
            <div class="campo-valore">
              <template v-if="campo.name === '_id' || campo.name === '_partition'">
                <span class="text-caption grey--text">(generato automaticamente)</span>
              </template>
              <template v-else-if="campo.tipo === 'Bool' || campo.tipo === 'Bool?'">
                <v-switch v-model="nuovoRecord[campo.name]" color="primary" hide-details dense class="mt-0" style="flex:none" />
              </template>
              <template v-else-if="campo.isOggetto">
                <div class="d-flex align-center">
                  <v-chip v-if="isRef(nuovoRecord[campo.name])" small color="primary">
                    {{ nuovoRecord[campo.name].__type }}
                    <span v-if="nuovoRecord[campo.name].__label" class="ml-1" style="opacity:0.8">&middot; {{ nuovoRecord[campo.name].__label }}</span>
                  </v-chip>
                  <span v-else class="text-body-2 grey--text">(nessuno)</span>
                  <v-btn outlined color="primary" class="ml-2" x-small style="min-width:36px"
                         @click="apriPickerRelazioneNuovo(campo.name)">…</v-btn>
                </div>
              </template>
              <template v-else>
                <v-text-field v-model="nuovoRecord[campo.name]"
                  :placeholder="campo.tipo"
                  outlined dense rounded hide-details class="campo-input" />
              </template>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small @click="dialogNuovoAperta = false">Annulla</v-btn>
          <v-btn color="success" depressed small rounded :loading="salvandoNuovo" @click="salvanuovo">
            <v-icon left small>mdi-content-save</v-icon>
            Crea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── DIALOG CONFERMA ELIMINA ──────────────────────────────────────── -->
    <v-dialog v-model="dialogEliminaAperta" max-width="420" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pa-6 text-center">
          <v-icon size="48" color="error" class="mb-3">mdi-delete-alert-outline</v-icon>
          <div class="text-h6 font-weight-bold mb-2">Elimina record</div>
          <div class="text-body-2 grey--text mb-1">Stai per eliminare un record da <strong>{{ classe }}</strong>.</div>
          <div class="text-caption font-mono grey--text">{{ rigaDaEliminare && rigaDaEliminare['_id'] }}</div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn text small @click="dialogEliminaAperta = false" :disabled="eliminando !== null">Annulla</v-btn>
          <v-spacer />
          <v-btn color="error" depressed small rounded :loading="eliminando !== null" @click="confermaElimina">
            <v-icon left small>mdi-delete-outline</v-icon>
            Elimina
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── DIALOG PICKER RELAZIONE ────────────────────────────────────── -->
    <v-dialog v-model="dialogRelazioneAperta" max-width="540" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
          <div class="d-flex align-center">
            <v-icon color="primary" size="20" class="mr-2">mdi-link-variant</v-icon>
            <span class="text-body-1 font-weight-bold">Scegli {{ dialogRelazioneTipo }}</span>
          </div>
          <v-btn icon small text @click="dialogRelazioneAperta = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-3" style="max-height:60vh">
          <v-text-field
            v-model="dialogRelazioneFiltro"
            placeholder="Cerca..."
            outlined
            dense
            rounded
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            class="mb-3"
          />

          <div v-if="dialogRelazioneLoading" class="text-center pa-6">
            <v-progress-circular indeterminate color="primary" size="28" />
          </div>
          <template v-else>
            <div
              v-for="rec in dialogRelazioneFiltrati"
              :key="rec['_id']"
              class="rel-item"
              @click="selezionaRelazione(rec)"
            >
              <div class="text-body-2 font-weight-medium">{{ getRelLabel(rec) || rec['_id'] }}</div>
              <div class="text-caption grey--text font-mono">{{ rec['_id'] }}</div>
            </div>
            <div v-if="!dialogRelazioneFiltrati.length" class="text-caption grey--text pa-3 text-center">
              Nessun risultato
            </div>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn outlined color="error" small @click="azzeraRelazione">
            <v-icon left small>mdi-link-off</v-icon>
            Imposta a null
          </v-btn>
          <v-spacer />
          <v-btn text small @click="dialogRelazioneAperta = false">Annulla</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
const FULCRO = '250613'
const SESSION_KEY = 'admin_realm_auth'
const BASE = process.env.VUE_APP_SERVER

async function apiGet(path, params) {
  let fullPath = (BASE || '') + path
  if (params) {
    const qs = new URLSearchParams(params).toString()
    if (qs) fullPath += '?' + qs
  }
  const res = await fetch(fullPath, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText))
  return res.json()
}

async function apiPost(path, body) {
  const res = await fetch(BASE + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText))
  return res.json()
}

export default {
  name: 'AdminRealmView',

  data() {
    return {
      // Auth
      autenticato:    sessionStorage.getItem(SESSION_KEY) === '1',
      passwordInput:  '',
      mostraPassword: false,
      errorePassword: false,

      // Partizioni
      listaPartizioni:   [],
      partizione:        null,
      loadingPartizioni: false,

      // Classi
      listaClassi:   [],
      classe:        null,
      loadingClassi: false,

      // Ricerca
      campoCercato:   null,
      valoreCercato:  '',
      risultati:      [],
      loadingRicerca: false,
      erroreRicerca:  '',
      cercatoMaVuoto: false,
      sortCol:        null,
      sortAsc:        true,

      // Dialog dettaglio
      dialogAperta:    false,
      rigaSelezionata: null,
      rigaEditabile:   {},
      salvando:        false,
      errSalvataggio:  '',

      // Elimina
      eliminando:          null,
      dialogEliminaAperta: false,
      rigaDaEliminare:     null,

      // Nuovo record
      dialogNuovoAperta: false,
      nuovoRecord:       {},
      salvandoNuovo:     false,
      errNuovo:          '',

      // Picker relazione
      dialogRelazioneAperta:  false,
      dialogRelazioneCampo:   '',
      dialogRelazioneTipo:    '',
      dialogRelazioneItems:   [],
      dialogRelazioneLoading: false,
      dialogRelazioneFiltro:  '',
      dialogRelazioneIsNuovo: false
    }
  },

  computed: {
    classeCorrente() {
      return this.listaClassi.find(c => c.nome === this.classe) || null
    },

    campiClasseCorrente() {
      return (this.classeCorrente?.campi || []).map(f => ({
        name: f.name,
        label: `${f.name} (${f.tipo})`,
        isOggetto: f.isOggetto,
        oggettoTipo: f.oggettoTipo
      }))
    },

    campoCercatoInfo() {
      return this.campiClasseCorrente.find(f => f.name === this.campoCercato) || null
    },

    colonneVisibili() {
      if (!this.risultati.length) return []
      return Object.keys(this.risultati[0])
    },

    risultatiOrdinati() {
      if (!this.sortCol) return this.risultati
      const col = this.sortCol
      const asc = this.sortAsc
      return [...this.risultati].sort((a, b) => {
        const va = a[col], vb = b[col]
        if (va == null) return 1
        if (vb == null) return -1
        const av = this.isRef(va) ? (va.__label || va.__type) : va
        const bv = this.isRef(vb) ? (vb.__label || vb.__type) : vb
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
        return asc ? cmp : -cmp
      })
    },

    haModifiche() {
      if (!this.rigaSelezionata) return false
      return Object.keys(this.rigaSelezionata).some(k => this.campoModificato(k))
    },

    campiNuovoRecord() {
      return (this.classeCorrente?.campi || []).filter(f =>
        !f.tipo.startsWith('[') && f.name !== 'LinkOriginPropertyName'
      )
    },

    dialogRelazioneFiltrati() {
      const q = (this.dialogRelazioneFiltro || '').trim().toLowerCase()
      if (!q) return this.dialogRelazioneItems
      return this.dialogRelazioneItems.filter(rec =>
        Object.values(rec).some(v =>
          v !== null && typeof v !== 'object' && String(v).toLowerCase().includes(q)
        )
      )
    }
  },

  mounted() {
    if (this.autenticato) this.caricaPartizioni()
  },

  methods: {
    // ── AUTH ────────────────────────────────────────────────────────────
    verificaPassword() {
      if (this.passwordInput === FULCRO) {
        sessionStorage.setItem(SESSION_KEY, '1')
        this.autenticato = true
        this.errorePassword = false
        this.caricaPartizioni()
      } else {
        this.errorePassword = true
      }
    },

    logout() {
      sessionStorage.removeItem(SESSION_KEY)
      this.autenticato = false
      this.passwordInput = ''
    },

    // ── PARTIZIONI ──────────────────────────────────────────────────────
    async caricaPartizioni() {
      this.loadingPartizioni = true
      try {
        const data = await apiGet('/api/Admin/partizioni', { fulcro: FULCRO })
        this.listaPartizioni = ['PUBLIC', ...data.filter(p => p !== 'PUBLIC')]
      } catch (err) {
        console.error('[Admin] caricaPartizioni error:', err)
        this.listaPartizioni = ['PUBLIC']
      } finally {
        this.loadingPartizioni = false
      }
    },

    onPartitionChange() {
      this.classe = null
      this.listaClassi = []
      this.risultati = []
      this.cercatoMaVuoto = false
      if (this.partizione) this.caricaClassi()
    },

    // ── CLASSI ──────────────────────────────────────────────────────────
    async caricaClassi() {
      this.loadingClassi = true
      try {
        const data = await apiGet('/api/Admin/classi', { partition: this.partizione, fulcro: FULCRO })
        this.listaClassi = data.map(c => ({ nome: c.nome, campi: c.campi }))
      } catch {
        this.listaClassi = []
      } finally {
        this.loadingClassi = false
      }
    },

    onClasseChange() {
      this.campoCercato = null
      this.valoreCercato = ''
      this.risultati = []
      this.cercatoMaVuoto = false
      if (this.classe) this.cerca()
    },

    // ── RICERCA ─────────────────────────────────────────────────────────
    async cerca() {
      if (!this.classe) return
      this.loadingRicerca = true
      this.erroreRicerca = ''
      this.cercatoMaVuoto = false
      this.risultati = []
      this.sortCol = null
      this.sortAsc = true
      try {
        const data = await apiPost('/api/Admin/cerca', {
          fulcro:    FULCRO,
          partition: this.partizione,
          classe:    this.classe,
          campo:     this.campoCercato || null,
          valore:    this.valoreCercato || null
        })
        this.risultati = data
        if (!data.length) this.cercatoMaVuoto = true
      } catch (err) {
        this.erroreRicerca = err.message || 'Errore durante la ricerca'
      } finally {
        this.loadingRicerca = false
      }
    },

    resetRicerca() {
      this.campoCercato = null
      this.valoreCercato = ''
      this.risultati = []
      this.cercatoMaVuoto = false
    },

    ordinaPer(col) {
      if (this.sortCol === col) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortCol = col
        this.sortAsc = true
      }
    },

    // ── DIALOG DETTAGLIO ─────────────────────────────────────────────────
    isCampoRelazione(campo) {
      return this.classeCorrente?.campi.some(f => f.name === campo && f.isOggetto) || false
    },

    apriDettaglio(row) {
      this.rigaSelezionata = row
      this.rigaEditabile = Object.fromEntries(
        Object.entries(row).map(([k, v]) => [k, v != null ? v : null])
      )
      this.errSalvataggio = ''
      this.dialogAperta = true
    },

    chiudiDialog() {
      this.dialogAperta = false
      this.errSalvataggio = ''
    },

    campoModificato(campo) {
      const orig = this.rigaSelezionata?.[campo]
      const edit = this.rigaEditabile[campo]
      if (this.isRef(orig) && this.isRef(edit)) return orig.__id !== edit.__id
      if (this.isRef(orig) || this.isRef(edit)) return true
      const origStr = orig == null ? '' : String(orig)
      const editStr = edit == null ? '' : String(edit)
      return origStr !== editStr
    },

    async salva() {
      if (!this.haModifiche) return
      this.salvando = true
      this.errSalvataggio = ''
      const modifiche = {}
      for (const k of Object.keys(this.rigaSelezionata)) {
        if (!this.campoModificato(k)) continue
        const editVal = this.rigaEditabile[k]
        if (this.isRef(editVal)) {
          modifiche[k] = editVal.__id
        } else if (editVal === null || editVal === '') {
          modifiche[k] = null
        } else {
          modifiche[k] = String(editVal)
        }
      }
      try {
        await apiPost('/api/Admin/aggiorna', {
          fulcro:    FULCRO,
          partition: this.partizione,
          classe:    this.classe,
          id:        this.rigaSelezionata['_id'],
          modifiche
        })
        Object.assign(this.rigaSelezionata, this.rigaEditabile)
        const idx = this.risultati.findIndex(r => r['_id'] === this.rigaSelezionata['_id'])
        if (idx !== -1) Object.assign(this.risultati[idx], this.rigaEditabile)
        this.chiudiDialog()
      } catch (err) {
        this.errSalvataggio = err.message || 'Errore durante il salvataggio'
      } finally {
        this.salvando = false
      }
    },

    navigaARef(ref) {
      this.dialogAperta = false
      this.classe = ref.__type
      this.campoCercato = '_id'
      this.valoreCercato = ref.__id || ''
      this.cerca()
    },

    copiaRiga() {
      navigator.clipboard.writeText(JSON.stringify(this.rigaSelezionata, null, 2))
    },

    // ── ELIMINA ──────────────────────────────────────────────────────────
    eliminaRiga(row) {
      this.rigaDaEliminare = row
      this.dialogEliminaAperta = true
    },

    async confermaElimina() {
      const row = this.rigaDaEliminare
      if (!row) return
      this.eliminando = row['_id']
      try {
        await apiPost('/api/Admin/elimina', {
          fulcro:    FULCRO,
          partition: this.partizione,
          classe:    this.classe,
          id:        row['_id']
        })
        this.risultati = this.risultati.filter(r => r['_id'] !== row['_id'])
        this.dialogEliminaAperta = false
      } catch (err) {
        alert(err.message || 'Errore durante l\'eliminazione')
      } finally {
        this.eliminando = null
        this.rigaDaEliminare = null
      }
    },

    // ── NUOVO RECORD ─────────────────────────────────────────────────────
    apriNuovo() {
      this.nuovoRecord = {}
      this.errNuovo = ''
      for (const campo of this.campiNuovoRecord) {
        if (campo.tipo === 'Bool' || campo.tipo === 'Bool?') {
          this.$set(this.nuovoRecord, campo.name, false)
        }
      }
      this.dialogNuovoAperta = true
    },

    async salvanuovo() {
      this.salvandoNuovo = true
      this.errNuovo = ''
      const campi = {}
      for (const [k, v] of Object.entries(this.nuovoRecord)) {
        if (this.isRef(v))      campi[k] = v.__id
        else if (v == null)     campi[k] = null
        else                    campi[k] = String(v)
      }
      try {
        await apiPost('/api/Admin/inserisci', {
          fulcro:    FULCRO,
          partition: this.partizione,
          classe:    this.classe,
          campi
        })
        this.dialogNuovoAperta = false
        await this.cerca()
      } catch (err) {
        this.errNuovo = err.message || 'Errore durante la creazione'
      } finally {
        this.salvandoNuovo = false
      }
    },

    // ── PICKER RELAZIONE ─────────────────────────────────────────────────
    async _apriPickerRelazioneBase(campo, isNuovo) {
      const schemaField = this.classeCorrente?.campi.find(f => f.name === campo)
      const tipoTarget  = schemaField?.oggettoTipo || ''
      if (!tipoTarget) return

      this.dialogRelazioneIsNuovo = isNuovo
      this.dialogRelazioneCampo   = campo
      this.dialogRelazioneTipo    = tipoTarget
      this.dialogRelazioneFiltro  = ''
      this.dialogRelazioneItems   = []
      this.dialogRelazioneAperta  = true
      this.dialogRelazioneLoading = true

      try {
        const data = await apiPost('/api/Admin/cerca', {
          fulcro:       FULCRO,
          partition:    this.partizione,
          classe:       tipoTarget,
          campo:        null,
          valore:       null,
          maxRisultati: 0
        })
        this.dialogRelazioneItems = data
      } catch {
        this.dialogRelazioneItems = []
      } finally {
        this.dialogRelazioneLoading = false
      }
    },

    apriPickerRelazione(campo)      { this._apriPickerRelazioneBase(campo, false) },
    apriPickerRelazioneNuovo(campo) { this._apriPickerRelazioneBase(campo, true)  },

    selezionaRelazione(rec) {
      const refObj = {
        __ref:   true,
        __type:  this.dialogRelazioneTipo,
        __id:    rec['_id'],
        __label: this.getRelLabel(rec)
      }
      if (this.dialogRelazioneIsNuovo) this.$set(this.nuovoRecord,   this.dialogRelazioneCampo, refObj)
      else                             this.$set(this.rigaEditabile, this.dialogRelazioneCampo, refObj)
      this.dialogRelazioneAperta = false
    },

    azzeraRelazione() {
      if (this.dialogRelazioneIsNuovo) this.$set(this.nuovoRecord,   this.dialogRelazioneCampo, null)
      else                             this.$set(this.rigaEditabile, this.dialogRelazioneCampo, null)
      this.dialogRelazioneAperta = false
    },

    getRelLabel(rec) {
      for (const f of ['Denominazione', 'Nome', 'Descrizione', 'Codice', 'Titolo', 'Title', 'Name']) {
        const v = rec[f]
        if (v && typeof v === 'string' && v.trim()) return v.trim()
      }
      return ''
    },

    // ── HELPERS ──────────────────────────────────────────────────────────
    isRef(val) {
      return val !== null && typeof val === 'object' && val.__ref === true
    },

    formatVal(val) {
      if (val === null || val === undefined) return ''
      if (this.isRef(val)) return `[${val.__type}]`
      const s = String(val)
      return s.length > 55 ? s.substring(0, 55) + '…' : s
    },

    copiaJSON() {
      navigator.clipboard.writeText(JSON.stringify(this.risultati, null, 2))
    }
  }
}
</script>

<style scoped>
.admin-table {
  border-collapse: collapse;
  font-size: 12px;
  white-space: nowrap;
  width: 100%;
}
.admin-table th {
  background: rgba(25, 118, 210, 0.08);
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  cursor: pointer;
  user-select: none;
}
.admin-table th:hover {
  background: rgba(25, 118, 210, 0.15);
}
.sort-icon {
  font-size: 9px;
  margin-left: 3px;
  opacity: 0.7;
}
.admin-table td {
  padding: 5px 10px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-table tr:last-child td { border-bottom: none; }
.admin-row { cursor: pointer; transition: background 0.15s; }
.admin-row:hover { background: rgba(25, 118, 210, 0.05); }

.campo-riga {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 6px 12px;
  align-items: start;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.campo-riga:last-child { border-bottom: none; }
.campo-nome { padding-top: 6px; }
.campo-input { font-size: 13px; }

.rel-item {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 2px;
}
.rel-item:hover {
  background: rgba(25, 118, 210, 0.08);
}
.font-mono { font-family: monospace; font-size: 11px; }
</style>
